/** How catalog stable listings are reviewed. */
export type AddonModerationMode = 'off' | 'precheck' | 'prefer_auto' | 'auto';

export const ADDON_MODERATION_MODES: AddonModerationMode[] = [
    'off',
    'precheck',
    'prefer_auto',
    'auto',
];

export const isAddonModerationMode = (value: unknown): value is AddonModerationMode =>
    value === 'off' || value === 'precheck' || value === 'prefer_auto' || value === 'auto';

/** LLM approve publishes immediately in `auto`, or in `prefer_auto` only when the agent is certain. */
export const addonLlmApprovePublishes = (mode: AddonModerationMode, certain?: boolean) =>
    mode === 'auto' || (mode === 'prefer_auto' && !!certain);

export type AddonModerationStatus = 'none' | 'queued' | 'running' | 'llm_approved' | 'rejected';

export const isAddonModerationStatus = (value: unknown): value is AddonModerationStatus =>
    value === 'none' ||
    value === 'queued' ||
    value === 'running' ||
    value === 'llm_approved' ||
    value === 'rejected';

export type AddonModerationSource = '' | 'script' | 'llm' | 'admin';

/** Rejection text shown to the author in the site language. */
export type AddonLocalizedReason = {
    en: string;
    ru: string;
    uk: string;
};

export const emptyAddonLocalizedReason = (): AddonLocalizedReason => ({
    en: '',
    ru: '',
    uk: '',
});

export const isAddonLocalizedReasonComplete = (value: AddonLocalizedReason | null | undefined) =>
    !!value &&
    value.en.trim().length > 0 &&
    value.ru.trim().length > 0 &&
    value.uk.trim().length > 0;

export const pickAddonLocalizedReason = (
    value: AddonLocalizedReason | null | undefined,
    lang: string,
) => {
    if (!value) {
        return '';
    }

    if (lang === 'ru') {
        return value.ru || value.en || value.uk;
    }

    if (lang === 'uk' || lang === 'ua') {
        return value.uk || value.en || value.ru;
    }

    return value.en || value.ru || value.uk;
};

export type AddonModerationPublic = {
    status: AddonModerationStatus;
    source: AddonModerationSource;
    reason: AddonLocalizedReason | null;
    llmRejected: boolean;
    error?: string;
    checkedAt?: number;
};

/** Built-in @xxanderwp/ai-conductor provider ids (no secrets). */
export const LLM_CONDUCTOR_PROVIDER_IDS = [
    'ollama',
    'gemini',
    'groq',
    'cerebras',
    'mistral',
    'nvidia',
    'github',
    'zai',
    'puter',
    'opencode',
    'huggingface',
    'openrouter',
    'cohere',
    'openai',
    'openai_compatible',
] as const;

export type LlmConductorProviderId = (typeof LLM_CONDUCTOR_PROVIDER_IDS)[number];

export const isLlmConductorProviderId = (value: unknown): value is LlmConductorProviderId =>
    typeof value === 'string' &&
    (LLM_CONDUCTOR_PROVIDER_IDS as readonly string[]).includes(value);

export type LlmProviderPublic = {
    id: LlmConductorProviderId;
    label: string;
    model: string;
    baseUrl: string;
    enabled: boolean;
    priority: number;
    dailyLimit: number;
    hasApiKey: boolean;
    apiKeyMasked: string;
};

export type LlmProviderCatalogItem = {
    id: LlmConductorProviderId;
    label: string;
    suggestedModels: string[];
    defaultBaseUrl: string;
    requiresApiKey: boolean;
};

export type AddonModerationAdminSettings = {
    mode: AddonModerationMode;
    extraPrompt: string;
    systemPrompt: string;
    providers: LlmProviderPublic[];
    availableProviders: LlmProviderCatalogItem[];
};
